import DataGridStriped from '@components/datagrid';
import { DataGridToolbar } from '@components/datagrid';
import CustomGridFilterPanel from '@components/datagrid/CustomGridFilterPanel';
import DataGridBox from '@components/datagrid/DataGridBox';
import container from '@ioc';
import { Download } from '@mui/icons-material';
import { IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid-pro';
import { ICommunicationService, ICommunicationService$ } from '@services/communication/ICommunication.interface';
import strings from '@src/app/strings/LocalisedStrings';
import { dateFormatter } from '@src/app/utils/GenericUtils';
import { DocumentSummary } from 'clientportalshared';
import { FC, useMemo, useState } from 'react';

const columns: GridColumns = [
  {
    field: 'name',
    headerName: strings.documentName,
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'type',
    headerName: strings.documentType,
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'createdOnUtc',
    headerName: strings.dateAdded,
    valueFormatter: (params) => dateFormatter(params.value?.toString() || ''),
    flex: 1,
    minWidth: 100,
  },
  {
    field: 'action',
    headerName: strings.action,
    minWidth: 150,
    renderCell: (params) => {
      const communicationService = container.get<ICommunicationService>(ICommunicationService$);

      const downloadDocument = () => {
        const url = params.row['url'];
        const name = params.row['name'];
        communicationService.downloadPdfAsync(url, `${name}`);
      };

      return (
        <Tooltip title="Download">
          <IconButton onClick={downloadDocument}>
            <Download />
          </IconButton>
        </Tooltip>
      );
    },
    filterable: false,
    sortable: false,
    hideable: false,
  },
];

interface DocumentsDataGridProps {
  data: DocumentSummary[];
  isLoading: boolean;
}

export const DocumentsDataGrid: FC<DocumentsDataGridProps> = ({ data, isLoading }: DocumentsDataGridProps) => {
  const [search, setSearch] = useState<string>('');
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.between('xs', 'lg'));

  const filteredRows = useMemo(() => {
    return data.filter((value) => value.name?.toLowerCase().includes(search.toLowerCase()) && value.displayOnInvestorPortal);
  }, [search]);

  const rows = useMemo(() => {
    return data.filter((value) => value.displayOnInvestorPortal);
  }, [data]);

  return (
    <DataGridBox>
      <DataGridStriped
        loading={isLoading}
        rows={search.length === 0 ? rows : filteredRows}
        componentsProps={{
          toolbar: {
            searchPlaceholder: 'Search by Name...',
            searchValue: search,
            setSearchValue: setSearch,
          },
        }}
        initialState={{
          pagination: {
            pageSize: small ? 10 : 25,
          },
        }}
        components={{
          Toolbar: DataGridToolbar,
          FilterPanel: CustomGridFilterPanel,
        }}
        localeText={{ noRowsLabel: strings.emptyDocumentDataMessage }}
        disableSelectionOnClick
        getRowId={(row) => row.documentKey}
        columns={columns}
      />
    </DataGridBox>
  );
};
